import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Helmet } from 'react-helmet';
import { LanguageContext } from '../../contexts/LanguageContext';
import { ThemeContext } from '../../contexts/ThemeContext';

const KubernetesConsulting = () => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
      easing: 'ease-out-cubic'
    });
    
    return () => {
      AOS.refresh();
    };
  }, []);
  return (
    <div className="py-16 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 min-h-screen">
      <Helmet>
        <title>{translations['kubernetes.pageTitle']}</title>
        <meta name="description" content={translations['kubernetes.hero.description']} />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-800 dark:text-blue-50 mb-8" data-aos="fade-up">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">{translations['kubernetes.title'].split(' ')[0]}</span> {translations['kubernetes.title'].split(' ')[1]}
          </h1>
          
          <div className="bg-gradient-to-r from-indigo-600 via-blue-700 to-teal-600 text-white p-8 md:p-10 rounded-xl shadow-xl mb-12 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl" data-aos="zoom-in">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">{translations['kubernetes.hero.title']}</h2>
            <p className="text-lg md:text-xl">
              {translations['kubernetes.hero.description']}
            </p>
          </div>
          
          <div className="mb-12" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <span className="inline-block mr-3 bg-blue-600 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </span>
              {translations['kubernetes.advisory.title']}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-6">
              {translations['kubernetes.advisory.description']}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-blue-600" data-aos="fade-right" data-aos-delay="100">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-blue-600 mr-2">⟡</span> {translations['kubernetes.roadmap.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['kubernetes.roadmap.description']}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600" data-aos="fade-left" data-aos-delay="200">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-teal-600 mr-2">⟡</span> {translations['kubernetes.architecture.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['kubernetes.architecture.description']}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-indigo-600" data-aos="fade-right" data-aos-delay="300">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-indigo-600 mr-2">⟡</span> {translations['kubernetes.implementation.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['kubernetes.implementation.description']}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-yellow-600" data-aos="fade-left" data-aos-delay="400">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-yellow-600 mr-2">⟡</span> {translations['kubernetes.migration.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['kubernetes.migration.description']}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-16" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-6 flex items-center">
              <span className="inline-block mr-3 bg-teal-600 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </span>
              {translations['kubernetes.spheres.title']}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-8">
              {translations['kubernetes.spheres.description']}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-6">
              <div className="flex items-start" data-aos="fade-up" data-aos-delay="100">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['kubernetes.cluster.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['kubernetes.cluster.description']}</p>
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['kubernetes.cluster.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['kubernetes.cluster.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-left" data-aos-delay="150">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-teal-600 to-teal-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['kubernetes.deployment.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['kubernetes.deployment.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-right" data-aos-delay="200">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-indigo-600 to-indigo-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['kubernetes.networking.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['kubernetes.networking.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-left" data-aos-delay="250">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-purple-600 to-purple-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['kubernetes.security.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['kubernetes.security.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-right" data-aos-delay="300">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-pink-600 to-pink-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['kubernetes.observability.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['kubernetes.observability.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-left" data-aos-delay="350">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-amber-600 to-amber-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['kubernetes.gitops.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['kubernetes.gitops.description']}</p>
                </div>
              </div>
              
              <div className="flex items-start" data-aos="fade-right" data-aos-delay="400">
                <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gradient-to-br from-green-600 to-green-700 text-white flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{translations['kubernetes.finops.title']}</h4>
                  <p className="text-gray-600 dark:text-gray-300">{translations['kubernetes.finops.description']}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12" data-aos="fade-up">
            <div className="inline-block relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-teal-600 rounded-lg blur opacity-60 group-hover:opacity-100 transition duration-300"></div>
              <Link 
                to="/contact-us" 
                className="relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white font-bold text-lg rounded-lg shadow-xl hover:shadow-2xl transform transition duration-300 hover:-translate-y-1"
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
                {translations['kubernetes.cta']}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KubernetesConsulting;
